import pluralize from "pluralize";
import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import styleLayout from "../../core/styles/PageLayout.module.scss";

import RefGenomeSelector from "../../entities/RefGenomeSelector.js";
import { VariantMutantGenomesSelectionWidget } from "../../genome-viewer/VariantMutantGenomesSelectionForm.js";
import { ContentPlaceholder } from "../ContentPlaceholder.js";
import { GroupButtonHeader } from "./ButtonHeaderHoc.js";
import PageHeader from "./PageHeader.js";
import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { capitalizeFirstLetterOfAllWords, pluralizeWord } from "@/app-utils/string.ts";
import { type Group, type InputTableQuerySpec, type Organism, type SequenceFeatureTypes } from "@/graphql.ts";
import { setGroupMutantGenomeIds } from "@/redux/modules/saved-group.ts";
import {
  type ITableInitializationOptions,
  createTableSpecForGroupParent,
  createUniqueKeyFromTableSpec,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, getSequenceFeatureTypeDisplayName } from "@/settings/settings.ts";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";

interface IGroupPageLayoutOwnProps {
  group: Group;
  tableInitializationOptions?: ITableInitializationOptions;
  documentTitleContext?: string;
}

const GroupPageLayout: FC<IGroupPageLayoutOwnProps> = (props) => {
  const viewId = useURLViewID();
  const dispatch = useDispatch<ReduxDispatch>();
  const refGenomeId = useSelector<IAppState, number>((state) => state.auth.session?.params?.referenceGenome);
  const mutantGenomeIds = useSelector<IAppState, number[]>((state) => state.savedGroup.visibleMutantGenomeIds);
  const sequenceFeatureTypes = useSelector<IAppState, SequenceFeatureTypes>(
    (state) => state.listing.sequenceFeatureTypes,
  );
  const [tableQuerySpec, setTableQuerySpec] = useState<InputTableQuerySpec>(null);
  const [groupLoadingErrorMessage, setGroupLoadingErrorMessage] = useState<string>("");
  const { group } = props;

  // cleanup of the selection of mutant genomes ids, since it might be incompatible with the next group's reference library
  useEffect(() => {
    return () => {
      dispatch(setGroupMutantGenomeIds([]));
    };
  }, []);

  useDocumentTitle(
    props.documentTitleContext ??
      `${group.name} [${group.itemCount || 0} ${pluralizeWord(group.typeDisplayName, group.itemCount)}]`,
  );

  useEffect(() => {
    let tableSpec: InputTableQuerySpec = createTableSpecForGroupParent(group.type, group.id, group.itemSubTypeId);
    // add dynamically the ref genome restriction
    if (
      (tableSpec.resultType === ENTITY_TYPE.MUTATION || tableSpec.resultType === ENTITY_TYPE.VARIANT) &&
      // we might show the variants track although the genome is NOT part of an analysis group (no ref genome set at all)
      group.analysisGroup?.refGenomes?.length > 0
    ) {
      const referenceGenomes: Organism[] = group.analysisGroup.refGenomes;
      if (refGenomeId && !!referenceGenomes.find((rg: Organism) => rg.id === refGenomeId)) {
        tableSpec = {
          ...tableSpec,
          restrictions: { ...tableSpec.restrictions, refGenomeId },
        };
      } else {
        tableSpec = null; // so that we do not trigger a request with incomplete or wrong data
      }
    } else if (tableSpec.resultType === ENTITY_TYPE.VCF_VARIANT) {
      tableSpec = {
        ...tableSpec,
        restrictions: {
          ...tableSpec.restrictions,
          variantGenomeIds: mutantGenomeIds,
        },
      };
    }
    setTableQuerySpec(tableSpec);
  }, [group, refGenomeId, mutantGenomeIds]);

  // code specific for the header of variants tables
  const getRefGenomeSelector = () => {
    // check if the active tab is of variant type
    if (group.type === ENTITY_TYPE.MUTATION || group.type === ENTITY_TYPE.VARIANT) {
      const referenceGenomes: Organism[] = group.analysisGroup?.refGenomes;
      // RefGenomeSelector accounts for missing ref genome and is not mounted
      return <RefGenomeSelector referenceGenomes={referenceGenomes} key={"group-page-layout-action-1"} />;
    }
    return null;
  };

  // code specific for the header of vcf variants tables
  const getMutantGenomeSelector = (tableQuerySpec: InputTableQuerySpec) => {
    if (tableQuerySpec && group.type === ENTITY_TYPE.VCF_VARIANT && group.analysisGroup) {
      return (
        <VariantMutantGenomesSelectionWidget
          variantAnalysisGroup={group.analysisGroup}
          tableQuerySpec={tableQuerySpec}
          //initiallySelectedMutantGenomeIds={[]}
        />
      );
    }
    return null;
  };

  const groupTitle: string = getSequenceFeatureTypeDisplayName(tableQuerySpec, sequenceFeatureTypes);

  return (
    <>
      <PageHeader
        title={groupTitle && capitalizeFirstLetterOfAllWords(pluralize.plural(groupTitle))}
        subtitle={"Group"}
        toolsWidgets={[getRefGenomeSelector(), getMutantGenomeSelector(tableQuerySpec)]}
        actionWidgets={[
          <GroupButtonHeader
            tableQuerySpec={tableQuerySpec}
            getParentEntityName={() => group.name}
            key={"group-page-layout-action-2"}
          />,
        ]}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        {groupLoadingErrorMessage && <ContentPlaceholder>{groupLoadingErrorMessage}</ContentPlaceholder>}

        {!groupLoadingErrorMessage &&
          tableQuerySpec && ( // variant table might not be ready in regard to the ref genome id logic
            <DataTableContainer
              key={createUniqueKeyFromTableSpec(tableQuerySpec)}
              inactive={
                false /* TODO necessary when we hide some views instead of unmounting/mounting =>activeKey !== this.getTabKey(collectionDescription)*/
              }
              tableSpec={tableQuerySpec}
              viewId={viewId}
              tableInitializationOptions={props.tableInitializationOptions}
              initialCount={group.itemCount}
            />
          )}
      </div>
    </>
  );
};

export default GroupPageLayout;
